import * as React from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

interface UserSecurityInfo {
  is_admin: boolean;
  admin_level: 'super_admin' | 'admin' | 'user';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isSigningOut: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  adminLevel: 'super_admin' | 'admin' | 'user';
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [securityInfo, setSecurityInfo] = React.useState<UserSecurityInfo | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const fetchUserProfile = async (userId: string, retryCount = 0): Promise<Profile | null> => {
    try {
      console.log(`Fetching profile for user ${userId}, attempt ${retryCount + 1}`);
      
      // Fetch basic profile data (now only contains public information)
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Profile fetch error:', error);
        
        // Retry up to 3 times for certain errors
        if (retryCount < 2 && (error.code === 'PGRST116' || error.message.includes('network'))) {
          console.log('Retrying profile fetch...');
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return fetchUserProfile(userId, retryCount + 1);
        }
        return null;
      }
      
      console.log('Profile loaded successfully:', profileData);
      return profileData;
    } catch (error) {
      console.error('Profile fetch exception:', error);
      return null;
    }
  };

  const fetchUserSecurityInfo = async (userId: string): Promise<UserSecurityInfo | null> => {
    try {
      console.log(`Fetching security info for user ${userId}`);
      
      const { data: securityData, error } = await supabase
        .from('user_security_info')
        .select('is_admin, admin_level')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Security info fetch error:', error);
        // Return default values if no security info found
        return { is_admin: false, admin_level: 'user' };
      }
      
      console.log('Security info loaded successfully:', securityData);
      return {
        is_admin: securityData.is_admin || false,
        admin_level: (securityData.admin_level || 'user') as 'super_admin' | 'admin' | 'user'
      };
    } catch (error) {
      console.error('Security info fetch exception:', error);
      return { is_admin: false, admin_level: 'user' };
    }
  };

  React.useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);
        
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer profile and security info fetch to avoid auth state change recursion
          setTimeout(async () => {
            if (!mounted) return;
            
            const [profileData, securityData] = await Promise.all([
              fetchUserProfile(session.user.id),
              fetchUserSecurityInfo(session.user.id)
            ]);
            
            if (mounted) {
              setProfile(profileData);
              setSecurityInfo(securityData);
              setIsLoading(false);
            }
          }, 100);
        } else {
          setProfile(null);
          setSecurityInfo(null);
          if (mounted) {
            setIsLoading(false);
          }
        }
      }
    );

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session fetch error:', error);
          if (mounted) {
            setIsLoading(false);
          }
          return;
        }
        
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const profileData = await fetchUserProfile(session.user.id);
          if (mounted) {
            setProfile(profileData);
            setIsLoading(false);
          }
        } else {
          if (mounted) {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        }
      }
    });
    return { error };
  };

  const signOut = async () => {
    // Prevent multiple simultaneous logout attempts
    if (isSigningOut) {
      return { error: null };
    }

    setIsSigningOut(true);
    
    try {
      // Check if we have a current session
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        setUser(null);
        setSession(null);
        setProfile(null);
        return { error: null };
      }
      
      // Try global signout first to revoke tokens on server
      const { error: globalError } = await supabase.auth.signOut({
        scope: 'global'
      });
      
      // Treat session missing errors as soft success
      if (globalError) {
        const isSessionMissingError = 
          globalError.name === 'AuthSessionMissingError' ||
          globalError.message?.includes('session_not_found') ||
          globalError.status === 400 ||
          globalError.status === 403;
          
        if (!isSessionMissingError) {
          console.error('Global logout failed, continuing with local cleanup:', globalError);
        }
      }
      
      // Always perform local cleanup regardless of global logout result
      
      // Local signout as fallback
      await supabase.auth.signOut({ scope: 'local' });
      
      // Hard fallback: manually remove auth token from localStorage
      const storageKey = 'sb-ropievbxucezuacvgpbk-auth-token';
      localStorage.removeItem(storageKey);
      
      // Clear local state
      setUser(null);
      setSession(null);
      setProfile(null);
      
      return { error: null };
      
    } catch (error) {
      console.error('Logout exception:', error);
      
      // Always clear local state even if everything fails
      setUser(null);
      setSession(null);
      setProfile(null);
      
      // Clear localStorage as last resort
      try {
        const storageKey = 'sb-ropievbxucezuacvgpbk-auth-token';
        localStorage.removeItem(storageKey);
      } catch (storageError) {
        console.error('Failed to clear localStorage:', storageError);
      }
      
      // Don't return error for common session issues
      const errorMessage = error?.message || '';
      if (errorMessage.includes('session_not_found') || errorMessage.includes('AuthSessionMissingError')) {
        return { error: null };
      }
      
      return { error };
    } finally {
      setIsSigningOut(false);
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password,
    });
    return { error };
  };

  const isAdmin = securityInfo?.is_admin ?? false;
  const isSuperAdmin = securityInfo?.admin_level === 'super_admin';
  const adminLevel = securityInfo?.admin_level ?? 'user';

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isLoading,
        isSigningOut,
        isAdmin,
        isSuperAdmin,
        adminLevel,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    // Return default values instead of throwing error
    return {
      user: null,
      session: null,
      profile: null,
      isLoading: false,
      isSigningOut: false,
      isAdmin: false,
      isSuperAdmin: false,
      adminLevel: 'user' as const,
      signIn: async () => ({ error: new Error('Auth not available') }),
      signUp: async () => ({ error: new Error('Auth not available') }),
      signOut: async () => ({ error: new Error('Auth not available') }),
      resetPassword: async () => ({ error: new Error('Auth not available') }),
      updatePassword: async () => ({ error: new Error('Auth not available') }),
    };
  }
  return context;
};